import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import ChipEditor from "@/components/ChipEditor";

const empty = { slug: "", name: "", short_description: "", long_description: "", price: 0, processing_time: "", required_documents: [], features: [], is_active: true, icon: "FileText", plans: [], sac_hsn: "998231", gst_rate: 18, pricing_type: "single", pricing_note: "", plan_display_style: "radio", show_on_homepage: true, home_order: 100, display_order: 100 };
const emptyPlan = { code: "", name: "", price: 0, description: "", popular: false, duration_years: 0 };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/services?active_only=false").then((r) => setServices(r.data.services));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s.slug);
    setForm({
      ...empty,
      ...s,
      required_documents: s.required_documents || [],
      features: s.features || [],
      plans: s.plans || [],
      pricing_type: s.pricing_type || (s.plans && s.plans.length ? "validity" : "single"),
      pricing_note: s.pricing_note || "",
    });
    setOpen(true);
  };

  const addPlan = () => setForm({ ...form, plans: [...(form.plans || []), { ...emptyPlan }] });
  const updatePlan = (i, field, value) => {
    const plans = [...(form.plans || [])];
    plans[i] = { ...plans[i], [field]: field === "price" ? Number(value) : value };
    setForm({ ...form, plans });
  };
  const removePlan = (i) => {
    const plans = [...(form.plans || [])];
    plans.splice(i, 1);
    setForm({ ...form, plans });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        pricing_type: form.pricing_type || "single",
        pricing_note: form.pricing_note || "",
        plans: form.pricing_type === "validity" ? (form.plans || []) : [],
        show_on_homepage: form.show_on_homepage !== false,
        home_order: Number(form.home_order ?? 100),
        display_order: Number(form.display_order ?? 100),
      };
      if (payload.pricing_type === "validity" && payload.plans.length === 0) {
        toast.error("Add at least one validity tier or switch to Single Price");
        return;
      }
      if (editing) {
        await api.put(`/admin/services/${editing}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/admin/services", payload);
        toast.success("Service created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/services/${s.slug}`, { ...s, is_active: !s.is_active });
      await load();
    } catch { toast.error("Failed"); }
  };

  return (
    <DashboardLayout admin>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="section-label">Admin</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Services</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={openNew} data-testid="svc-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit service" : "Create service"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Slug</Label><Input required value={form.slug} disabled={!!editing} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="svc-form-slug" /></div>
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-form-name" /></div>
                <div><Label>Price (₹)</Label><Input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="svc-form-price" /></div>
                <div><Label>Processing Time</Label><Input required value={form.processing_time} onChange={(e) => setForm({ ...form, processing_time: e.target.value })} data-testid="svc-form-time" /></div>
                <div><Label>SAC / HSN Code</Label><Input value={form.sac_hsn || ""} onChange={(e) => setForm({ ...form, sac_hsn: e.target.value })} placeholder="998231" className="font-mono" data-testid="svc-form-sac" /></div>
                <div><Label>GST Rate (%)</Label><Input type="number" value={form.gst_rate ?? 18} onChange={(e) => setForm({ ...form, gst_rate: parseFloat(e.target.value) || 0 })} data-testid="svc-form-gst-rate" /></div>
              </div>
              <div><Label>Short description</Label><Input required value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
              <div><Label>Long description</Label><Textarea rows={3} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></div>
              <div><Label>Required documents</Label>
                <ChipEditor value={form.required_documents} onChange={(v) => setForm({ ...form, required_documents: v })} placeholder="Add a document (e.g. PAN Card) then press Enter" testid="svc-form-docs" />
                <div className="text-xs text-slate-500 mt-1">These appear on the customer's order page as required uploads for this service.</div>
              </div>
              <div><Label>Features</Label>
                <ChipEditor value={form.features} onChange={(v) => setForm({ ...form, features: v })} placeholder="Add a feature bullet, then press Enter" testid="svc-form-features" />
              </div>

              <div className="pt-3 border-t border-slate-100">
                {/* Pricing type toggle */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Pricing Type</Label>
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="svc-pricing-type"
                        checked={form.pricing_type === "single"}
                        onChange={() => setForm({ ...form, pricing_type: "single", plans: [] })}
                        data-testid="svc-form-pricing-single"
                      />
                      Single Price
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="svc-pricing-type"
                        checked={form.pricing_type === "validity"}
                        onChange={() => setForm({ ...form, pricing_type: "validity" })}
                        data-testid="svc-form-pricing-validity"
                      />
                      Validity-based Pricing
                    </label>
                  </div>
                  <div className="mt-3">
                    <Label>Pricing note (shown on service page & checkout)</Label>
                    <Input
                      value={form.pricing_note || ""}
                      onChange={(e) => setForm({ ...form, pricing_note: e.target.value })}
                      placeholder="e.g. One Time · Excluding Govt. Fee · Excluding GST"
                      data-testid="svc-form-pricing-note"
                    />
                  </div>
                  {/* Plan Display Style */}
                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Plan Display Style (on /services page)</Label>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="svc-plan-display"
                          checked={(form.plan_display_style || "radio") === "radio"}
                          onChange={() => setForm({ ...form, plan_display_style: "radio" })}
                          data-testid="svc-form-display-radio"
                        />
                        Radio Buttons
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="svc-plan-display"
                          checked={form.plan_display_style === "dropdown"}
                          onChange={() => setForm({ ...form, plan_display_style: "dropdown" })}
                          data-testid="svc-form-display-dropdown"
                        />
                        Dropdown Menu
                      </label>
                    </div>
                  </div>
                </div>

                {form.pricing_type === "validity" && (
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label>Validity tiers / Pricing Plans</Label>
                      <div className="text-xs text-slate-500">Each tier is a validity option (e.g. 1 Year · ₹1999, 2 Years · ₹2499). The customer picks one on the apply page.</div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addPlan} data-testid="add-plan-btn">+ Add tier</Button>
                  </div>
                )}
                {form.pricing_type === "validity" && (form.plans || []).length === 0 && (
                  <div className="text-xs text-slate-500 p-3 rounded-md border border-dashed border-slate-200 bg-slate-50">
                    No validity tiers yet — click "Add tier" to create one.
                  </div>
                )}
                {form.pricing_type === "validity" && (
                <div className="space-y-3">
                  {(form.plans || []).map((p, i) => (
                    <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`plan-row-${i}`}>
                      <div className="grid sm:grid-cols-[1fr_2fr_100px] gap-2">
                        <Input placeholder="code (e.g. basic-1y)" value={p.code} onChange={(e) => updatePlan(i, "code", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className="font-mono text-xs" data-testid={`plan-code-${i}`} />
                        <Input placeholder="Display name (e.g. Basic — 1 Year)" value={p.name} onChange={(e) => updatePlan(i, "name", e.target.value)} data-testid={`plan-name-${i}`} />
                        <Input type="number" placeholder="Price" value={p.price} onChange={(e) => updatePlan(i, "price", e.target.value)} data-testid={`plan-price-${i}`} />
                      </div>
                      <div className="mt-2 flex gap-2 items-start">
                        <Input placeholder="Short description shown under plan name (optional)" value={p.description || ""} onChange={(e) => updatePlan(i, "description", e.target.value)} data-testid={`plan-desc-${i}`} />
                        <Button type="button" variant="outline" size="sm" onClick={() => removePlan(i)} data-testid={`plan-remove-${i}`}>Remove</Button>
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-2 items-center">
                        <label className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={!!p.popular} onChange={(e) => updatePlan(i, "popular", e.target.checked)} data-testid={`plan-popular-${i}`} />
                          Mark as "Most Popular" (ribbon)
                        </label>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-600 whitespace-nowrap">Duration (years):</span>
                          <Input type="number" min="0" value={p.duration_years || 0} onChange={(e) => updatePlan(i, "duration_years", Number(e.target.value))} data-testid={`plan-years-${i}`} className="h-8 w-20" />
                        </div>
                      </div>
                      {/* Enable quantity selection on the customer-facing service page */}
                      <div className="mt-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={!!p.quantity_enabled}
                            onChange={(e) => updatePlan(i, "quantity_enabled", e.target.checked)}
                            data-testid={`plan-qty-enabled-${i}`}
                          />
                          Enable Quantity Selection <span className="text-slate-500">(customer picks 1–99, total = price × qty)</span>
                        </label>
                      </div>
                      {/* Per-plan pricing type toggle */}
                      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pricing Type</div>
                        <div className="flex items-center gap-4 text-xs">
                          <label className="inline-flex items-center gap-1.5">
                            <input
                              type="radio"
                              name={`plan-ptype-${i}`}
                              checked={(p.pricing_type || "single") === "single"}
                              onChange={() => updatePlan(i, "pricing_type", "single")}
                              data-testid={`plan-${i}-ptype-single`}
                            />
                            Single Price
                          </label>
                          <label className="inline-flex items-center gap-1.5">
                            <input
                              type="radio"
                              name={`plan-ptype-${i}`}
                              checked={p.pricing_type === "validity"}
                              onChange={() => updatePlan(i, "pricing_type", "validity")}
                              data-testid={`plan-${i}-ptype-validity`}
                            />
                            Validity Based (5 years)
                          </label>
                        </div>
                        {p.pricing_type === "validity" && (
                          <div className="mt-2 grid grid-cols-5 gap-2">
                            {[1, 2, 3, 4, 5].map((y) => (
                              <div key={y}>
                                <div className="text-[10px] text-slate-500 mb-0.5">Year {y}</div>
                                <Input
                                  type="number"
                                  placeholder="0"
                                  value={(p.year_prices || {})[String(y)] ?? (p.year_prices || {})[y] ?? ""}
                                  onChange={(e) => {
                                    const yp = { ...(p.year_prices || {}) };
                                    const v = e.target.value;
                                    if (v === "") delete yp[String(y)]; else yp[String(y)] = Number(v);
                                    updatePlan(i, "year_prices", yp);
                                  }}
                                  className="h-8 text-xs"
                                  data-testid={`plan-${i}-year-${y}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>

              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="svc-form-active" /> <span className="text-sm">Active</span></div>

              {/* Homepage & ordering controls */}
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold text-sm text-[#0A192F]">Show on Homepage</div>
                    <div className="text-xs text-slate-500">When off, this service only appears on the Services page.</div>
                  </div>
                  <Switch
                    checked={form.show_on_homepage !== false}
                    onCheckedChange={(v) => setForm({ ...form, show_on_homepage: v })}
                    data-testid="svc-form-show-home"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Homepage Order</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.home_order ?? 100}
                      onChange={(e) => setForm({ ...form, home_order: Number(e.target.value) })}
                      data-testid="svc-form-home-order"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Lower number = shows first on homepage.</div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Services Page Order</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.display_order ?? 100}
                      onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })}
                      data-testid="svc-form-display-order"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Lower number = shows first on /services.</div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="svc-form-save-btn">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.slug} className="card-crisp rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-semibold text-lg text-[#0A192F]">{s.name}</div>
                <div className="font-mono text-xs text-slate-500">{s.slug}</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} />
              </div>
            </div>
            <div className="text-sm text-slate-600 mt-2 line-clamp-2">{s.short_description}</div>
            <div className="mt-4 flex items-center justify-between">
              <div className="font-bold text-[#0A192F]">₹{s.price}</div>
              <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`svc-edit-${s.slug}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
